import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Table,
  Tag,
  Wrap,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCanManageSourcingEvent } from "../useCanManageSourcingEvent";
import { useState } from "react";
import { runsUnderUtilities } from "./runsUnderUtilities";
import { useTranslation } from "react-i18next";
import { ReasonModal } from "../../../common/ReasonModal";
import { FiChevronRight, FiUsers } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  ParticipationRequestStatus,
  type BuyerParticipationRequestDto,
  type SourcingEventDto,
  useExtendDpsAssessmentWindowMutation,
  useListParticipationRequestsForBuyerQuery,
  useRemoveFromDpsSchemeMutation,
  useSetQualificationSchemeMemberCategoriesMutation,
  useWithdrawSchemeRemovalMutation,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { participationStatusBadge } from "../../../common/qualification/participationStatusBadge";
import { urls } from "../../../urls";
import { isQualificationScheme } from "../../sourcing-events/buyer/wizard/utils/procedure";
import { REVEAL_ANCHORS } from "../revealAnchor";
import {
  REMOVAL_NOTICE_DAYS,
  daysOfNotice,
  defaultLastDayOnList,
  schemeLastDayOnList,
  lastDayOnListBefore,
  removalTakesEffectAt,
  toDateInput,
} from "./removalNotice";

interface Props {
  event: SourcingEventDto;
  schemeOpen: boolean;
}

// The ordning's supplier register (FOA § 26-6): who is admitted and since when, alongside the
// applications still being assessed and the memberships that ended. It is the participation-request
// list — the register is a reading of it, not a second store.
export const DpsMembersTab = ({ event, schemeOpen }: Props) => {
  const { t } = useTranslation();
  const publicContext =
    event.eventContext.type === "Public" ? event.eventContext : null;
  const qualScheme = isQualificationScheme(publicContext?.procedureType);
  const { data, isLoading, isError, refetch } = useListParticipationRequestsForBuyerQuery({
    eventId: event.id,
  });
  const [removing, setRemoving] = useState<BuyerParticipationRequestDto | null>(
    null,
  );
  const [extending, setExtending] =
    useState<BuyerParticipationRequestDto | null>(null);
  const [placing, setPlacing] = useState<BuyerParticipationRequestDto | null>(
    null,
  );
  const categories = qualScheme
    ? (publicContext?.qualificationSchemeCategories ?? [])
    : [];
  const withdrawRemoval = useWithdrawRemoval(event.id);
  const mayManage = useCanManageSourcingEvent(event);

  if (isLoading) {
    return (
      <Stack spacing="4" aria-busy>
        <Skeleton height="10" rounded="md" />
        <Skeleton height="32" rounded="xl" />
      </Stack>
    );
  }
  if (isError || !data) {
    return (
      <Flex direction="column" align="center" py="10" gap="3">
        <Text color="smError">{t("Could not load the supplier list. Try again.")}</Text>
        <Button variant="outline" onClick={() => refetch()}>
          {t("Try again")}
        </Button>
      </Flex>
    );
  }

  const requests = data.requests ?? [];
  const qualified = requests.filter((r) => r.admitted);
  const awaiting = requests.filter(
    (r) => r.status === ParticipationRequestStatus.Submitted,
  );

  if (requests.length === 0) {
    return (
      <Card
        variant="outline"
        rounded="xl"
        bg="smBackground"
        borderColor="smBorder"
      >
        <CardBody py="10">
          <Flex direction="column" align="center" gap="3" textAlign="center">
            <Icon as={FiUsers} boxSize="8" color="smPrimary" />
            <Text fontWeight="semibold" color="smText">
              {t("No suppliers have applied yet")}
            </Text>
            <Text fontSize="sm" color="smMuted" maxW="md">
              {qualScheme
                ? t(
                    "Suppliers can apply for as long as the qualification system runs, and each application is assessed on its own.",
                  )
                : t(
                    "Suppliers can apply for as long as the system runs, and each application is assessed on its own.",
                  )}
            </Text>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  return (
    <Stack
      id={REVEAL_ANCHORS.dpsApplications}
      sx={{ scrollMarginTop: "1rem" }}
      spacing="5"
    >
      <Text fontSize="sm" color="smMuted">
        {awaiting.length > 0
          ? t("{{count}} qualified. {{awaiting}} awaiting assessment.", {
              count: qualified.length,
              awaiting: awaiting.length,
            })
          : qualScheme
            ? t("{{count}} qualified suppliers are on the supplier list.", {
                count: qualified.length,
              })
            : t(
                "{{count}} qualified suppliers are invited to every new competition.",
                { count: qualified.length },
              )}
      </Text>

      <Box
        bg="smBackground"
        border="1px solid"
        borderColor="smBorder"
        rounded="xl"
        overflowX="auto"
      >
        <Table size="md">
          <Thead>
            <Tr>
              <Th>{t("Supplier")}</Th>
              <Th>{t("Status")}</Th>
              <Th>{t("Qualified since")}</Th>
              {categories.length > 0 && <Th>{t("Categories")}</Th>}
              <Th>{t("Assessment deadline")}</Th>
              <Th aria-hidden />
            </Tr>
          </Thead>
          <Tbody>
            {requests.map((request) => (
              <MemberRow
                key={request.id}
                event={event}
                request={request}
                schemeOpen={schemeOpen}
                mayManage={mayManage}
                showsCategories={categories.length > 0}
                onRemove={() => setRemoving(request)}
                onWithdrawRemoval={() => withdrawRemoval.ask(request)}
                onExtend={() => setExtending(request)}
                onPlace={() => setPlacing(request)}
              />
            ))}
          </Tbody>
        </Table>
      </Box>

      {withdrawRemoval.modal}
      {removing && (
        <RemoveMemberModal
          eventId={event.id}
          request={removing}
          givesNotice={qualScheme}
          schemeLastDay={schemeLastDayOnList(publicContext?.qualificationScheme?.validTo)}
          onClose={() => setRemoving(null)}
        />
      )}
      {placing && (
        <PlaceInCategoriesModal
          eventId={event.id}
          request={placing}
          categories={categories}
          onClose={() => setPlacing(null)}
        />
      )}
      {extending && (
        <ExtendAssessmentModal
          eventId={event.id}
          underUtilities={runsUnderUtilities(event)}
          request={extending}
          onClose={() => setExtending(null)}
        />
      )}
    </Stack>
  );
};

const MemberRow = ({
  event,
  request,
  schemeOpen,
  mayManage,
  showsCategories,
  onRemove,
  onWithdrawRemoval,
  onExtend,
  onPlace,
}: {
  event: SourcingEventDto;
  request: BuyerParticipationRequestDto;
  schemeOpen: boolean;
  mayManage: boolean;
  showsCategories: boolean;
  onRemove: () => void;
  onWithdrawRemoval: () => void;
  onExtend: () => void;
  onPlace: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const badge = participationStatusBadge(request.status);
  const awaiting = request.status === ParticipationRequestStatus.Submitted;
  const publicContext =
    event.eventContext.type === "Public" ? event.eventContext : null;
  const monthClock = isQualificationScheme(publicContext?.procedureType);
  const pendingRemoval =
    request.admitted && request.status === ParticipationRequestStatus.Removed;
  const refusalSentAt =
    monthClock && request.status === ParticipationRequestStatus.Rejected
      ? request.qualificationVerdict?.decidedAt
      : undefined;

  const openRequest = () =>
    navigate(
      urls.app.sourcingEvents.participationRequestDetail.go(
        event.id,
        request.id,
      ),
    );

  return (
    <Tr
      _hover={{ bg: "smBackgroundSecondary" }}
      cursor="pointer"
      onClick={openRequest}
    >
      <Td>
        <Text fontWeight="semibold" fontSize="sm" color="smText" noOfLines={1}>
          {request.organizationName ?? request.organizationId}
        </Text>
        {request.organizationNumber && (
          <Text fontSize="xs" color="smMuted" fontFamily="mono">
            {request.organizationNumber}
          </Text>
        )}
      </Td>
      <Td>
        <HStack spacing="2" flexWrap="wrap">
          <Badge colorScheme={badge.colorScheme} fontSize="xs">
            {t(badge.labelKey)}
          </Badge>
          {request.membershipEndReason && (
            <Text fontSize="xs" color="smMuted" noOfLines={1} maxW="72">
              {request.membershipEndReason}
            </Text>
          )}
        </HStack>
        {pendingRemoval && request.membershipEndsAt && (
          <Text fontSize="xs" color="smChipAmberFg" mt="1">
            {t("On the list up to and including {{date}}", {
              date: displayDate(
                lastDayOnListBefore(request.membershipEndsAt).toISOString(),
              ),
            })}
          </Text>
        )}
        {refusalSentAt && (
          <Text fontSize="xs" color="smMuted" mt="1">
            {t(
              "Reason sent with the decision, {{date}} — forsyningsforskriften § 13-2 allows 15 days",
              {
                date: displayDate(refusalSentAt),
              },
            )}
          </Text>
        )}
      </Td>
      <Td fontSize="sm" color="smMuted" whiteSpace="nowrap">
        {request.status === ParticipationRequestStatus.Qualified &&
        request.qualificationVerdict
          ? displayDate(request.qualificationVerdict.decidedAt)
          : "–"}
      </Td>
      {showsCategories && (
        <Td onClick={(e) => e.stopPropagation()}>
          <Wrap spacing="1">
            {(request.categories ?? []).map((category) => (
              <Tag key={category} size="sm">
                {category}
              </Tag>
            ))}
            {mayManage && request.admitted && (
              <Button
                size="xs"
                variant="link"
                colorScheme="teal"
                onClick={onPlace}
              >
                {(request.categories ?? []).length > 0
                  ? t("Change")
                  : t("Choose categories")}
              </Button>
            )}
          </Wrap>
        </Td>
      )}
      <Td whiteSpace="nowrap">
        {awaiting ? (
          monthClock ? (
            <MonthClock request={request} />
          ) : (
            <AssessmentCountdown request={request} />
          )
        ) : (
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
        )}
      </Td>
      {/* The row opens the application; the buttons act on it in place, so they keep their click
          to themselves. */}
      <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
        <HStack spacing="1" justify="flex-end">
          {/* Assessing is what an awaiting application is waiting for, so it is named rather than
              left to the chevron. */}
          {mayManage && awaiting && (
            <Button size="sm" colorScheme="teal" onClick={openRequest}>
              {t("Assess")}
            </Button>
          )}
          {/* Names its object: every other action in the row acts on the supplier, so a bare
              "Utvid" reads as extending the membership rather than the clock next to it. */}
          {mayManage &&
            awaiting &&
            !monthClock &&
            !request.assessmentExtended &&
            !assessmentDeadlinePassed(request) &&
            schemeOpen && (
              <Button size="sm" variant="ghost" onClick={onExtend}>
                {t("Extend deadline")}
              </Button>
            )}
          {mayManage &&
            request.status === ParticipationRequestStatus.Qualified &&
            schemeOpen && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={onRemove}
              >
                {t("Remove")}
              </Button>
            )}
          {mayManage && pendingRemoval && monthClock && (
            <Button size="sm" variant="ghost" onClick={onWithdrawRemoval}>
              {t("Withdraw removal")}
            </Button>
          )}
          <Icon as={FiChevronRight} color="smMuted" />
        </HStack>
      </Td>
    </Tr>
  );
};

const assessmentDeadlinePassed = (request: BuyerParticipationRequestDto) =>
  request.assessmentDeadline != null
    ? new Date(request.assessmentDeadline).getTime() < Date.now()
    : (request.assessmentWorkingDaysRemaining ?? 0) < 0;

// § 26-6: ten working days from receipt, fifteen once extended. Overdue is stated plainly rather
// than hidden — the buyer is already late and the number is what tells them.
const AssessmentCountdown = ({
  request,
}: {
  request: BuyerParticipationRequestDto;
}) => {
  const { t } = useTranslation();
  const remaining = request.assessmentWorkingDaysRemaining;
  if (remaining == null)
    return (
      <Text fontSize="sm" color="smMuted">
        –
      </Text>
    );

  const overdue = remaining < 0;
  return (
    <HStack spacing="2">
      <Text
        fontSize="sm"
        color={
          overdue ? "smError" : remaining <= 2 ? "smWarningText" : "smText"
        }
      >
        {overdue
          ? t("{{count}} working days overdue", { count: Math.abs(remaining) })
          : t("{{count}} working days left", { count: remaining })}
      </Text>
      {request.assessmentExtended && (
        <Badge colorScheme="purple" fontSize="xs">
          {t("Extended")}
        </Badge>
      )}
    </HStack>
  );
};

// Forsyningsforskriften § 13-2: decide within four months; taking longer must be flagged to the
// applicant within two, and decided within six.
const MonthClock = ({ request }: { request: BuyerParticipationRequestDto }) => {
  const { t } = useTranslation();
  if (!request.submittedAt)
    return (
      <Text fontSize="sm" color="smMuted">
        –
      </Text>
    );

  const months = monthsSince(request.submittedAt);
  const color =
    months >= 4 ? "smError" : months >= 2 ? "smWarningText" : "smText";
  return (
    <Box>
      <Text fontSize="sm" color={color}>
        {t("{{count}} of four months used", { count: months })}
      </Text>
      {months >= 4 ? (
        <Text fontSize="xs" color="smError">
          {t("Decide now — six months after receipt is the outer limit")}
        </Text>
      ) : months >= 2 ? (
        <Text fontSize="xs" color="smWarningText">
          {t("Taking more than four months must be flagged to the applicant")}
        </Text>
      ) : null}
    </Box>
  );
};

// End-of-month clamp: an application from the 31st completes its Nth month on the last day of a
// shorter month, not three days into the next one — the § 13-2 escalation must not show late.
const monthsSince = (iso: string): number => {
  const then = new Date(iso);
  const now = new Date();
  const months =
    (now.getFullYear() - then.getFullYear()) * 12 +
    (now.getMonth() - then.getMonth());
  const daysInCurrentMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
  ).getDate();
  const dueDay = Math.min(then.getDate(), daysInCurrentMonth);
  return now.getDate() < dueDay ? Math.max(0, months - 1) : Math.max(0, months);
};

const PlaceInCategoriesModal = ({
  eventId,
  request,
  categories,
  onClose,
}: {
  eventId: string;
  request: BuyerParticipationRequestDto;
  categories: string[];
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [chosen, setChosen] = useState<string[]>(request.categories ?? []);
  const [place, { isLoading }] =
    useSetQualificationSchemeMemberCategoriesMutation();

  const submit = async () => {
    try {
      await place({
        eventId,
        requestId: request.id,
        schemeMemberCategoriesRequestDto: { categories: chosen },
      }).unwrap();
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Modal isOpen onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("Categories for {{supplier}}", {
            supplier: request.organizationName ?? request.organizationId,
          })}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="2">
            {categories.map((category) => (
              <Checkbox
                key={category}
                isChecked={chosen.includes(category)}
                onChange={(e) =>
                  setChosen((current) =>
                    e.target.checked
                      ? [...current, category]
                      : current.filter((c) => c !== category),
                  )
                }
              >
                {category}
              </Checkbox>
            ))}
          </Stack>
        </ModalBody>
        <ModalFooter
          gap="2"
          justifyContent="flex-start"
          flexDirection="row-reverse"
        >
          <Button colorScheme="teal" isLoading={isLoading} onClick={submit}>
            {t("Save")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const useWithdrawRemoval = (eventId: string) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const confirmation = useConfirmationModal();
  const [withdraw] = useWithdrawSchemeRemovalMutation();

  const ask = (request: BuyerParticipationRequestDto) =>
    confirmation.show({
      title: t("Withdraw the removal") ?? "",
      description: t(
        "{{supplier}} stays on the supplier list, in the categories it has now, and is told that the removal is withdrawn. To remove it on a different day, withdraw the removal and remove it again, which gives a new notice.",
        { supplier: request.organizationName ?? request.organizationId },
      ),
      confirmButtonText: t("Withdraw removal") ?? undefined,
      size: "lg",
      onConfirmed: async () => {
        try {
          await withdraw({ eventId, requestId: request.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });

  return { ask, modal: confirmation.modal };
};

const RemoveMemberModal = ({
  eventId,
  request,
  givesNotice,
  schemeLastDay,
  onClose,
}: {
  eventId: string;
  request: BuyerParticipationRequestDto;
  givesNotice: boolean;
  schemeLastDay: string | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const displayDate = useDisplayDate();
  const [reason, setReason] = useState("");
  const [lastDay, setLastDay] = useState(() =>
    defaultLastDayOnList(new Date(), schemeLastDay),
  );
  const [remove, { isLoading }] = useRemoveFromDpsSchemeMutation();
  const notice = lastDay ? daysOfNotice(new Date(), lastDay) : null;
  const dateMissing = givesNotice && notice === null;
  const afterSchemeEnds = givesNotice && !!lastDay && !!schemeLastDay && lastDay > schemeLastDay;
  const dateInPast = givesNotice && notice !== null && notice < 0;

  const submit = async () => {
    try {
      await remove({
        eventId,
        requestId: request.id,
        removeFromSchemeRequestDto: givesNotice
          ? { reason, effectiveAt: removalTakesEffectAt(lastDay) }
          : { reason },
      }).unwrap();
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <ReasonModal
      title={givesNotice ? t("Remove from the qualification system") : t("Remove from the system")}
      description={
        givesNotice
          ? t(
              "{{supplier}} is told now, with your reason, and stays on the supplier list up to and including the day below. After that it is not invited to new competitions.",
              { supplier: request.organizationName ?? request.organizationId },
            )
          : t(
              "{{supplier}} stops being invited to new competitions. Competitions already open keep the suppliers they were opened with.",
              { supplier: request.organizationName ?? request.organizationId },
            )
      }
      placeholder={t("Reason — sent to the supplier") ?? undefined}
      confirmLabel={t("Remove")}
      confirmColorScheme="red"
      reason={reason}
      onReasonChange={setReason}
      isLoading={isLoading}
      onConfirm={submit}
      onClose={onClose}
      isConfirmDisabled={dateMissing || dateInPast || afterSchemeEnds}
    >
      {givesNotice && (
        <FormControl mt="4" isRequired isInvalid={dateMissing || dateInPast || afterSchemeEnds}>
          <FormLabel htmlFor="removal-last-day" fontSize="sm">
            {t("Last day on the supplier list")}
          </FormLabel>
          <Input
            id="removal-last-day"
            type="date"
            min={toDateInput(new Date())}
            max={schemeLastDay ?? undefined}
            value={lastDay}
            onChange={(e) => setLastDay(e.target.value)}
          />
          {dateMissing && (
            <FormErrorMessage>
              {t("Choose the last day the supplier is on the list.")}
            </FormErrorMessage>
          )}
          {dateInPast && (
            <FormErrorMessage>
              {t("The last day can't be before today. Choose today or a later day.")}
            </FormErrorMessage>
          )}
          {afterSchemeEnds && schemeLastDay && (
            <FormErrorMessage>
              {t("The system runs until {{date}}, so the supplier can't stay on the list after that.", {
                date: displayDate(schemeLastDay),
              })}
            </FormErrorMessage>
          )}
          {notice === 0 && (
            <Alert status="warning" mt="2" rounded="md" fontSize="sm">
              <AlertIcon />
              {t(
                "The supplier leaves the list when today ends, with no notice. Forsyningsforskriften § 13-2 requires at least 15 days.",
              )}
            </Alert>
          )}
          {notice !== null && notice > 0 && notice < REMOVAL_NOTICE_DAYS && (
            <Alert status="warning" mt="2" rounded="md" fontSize="sm">
              <AlertIcon />
              {t(
                "That gives {{count}} days of notice. Forsyningsforskriften § 13-2 requires at least 15 days before a removal takes effect.",
                { count: notice },
              )}
            </Alert>
          )}
        </FormControl>
      )}
    </ReasonModal>
  );
};

export const ExtendAssessmentModal = ({
  eventId,
  underUtilities,
  request,
  onClose,
}: {
  eventId: string;
  underUtilities: boolean;
  request: BuyerParticipationRequestDto;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [reason, setReason] = useState("");
  const [extend, { isLoading }] = useExtendDpsAssessmentWindowMutation();

  const submit = async () => {
    try {
      await extend({
        eventId,
        requestId: request.id,
        extendAssessmentRequestDto: { reason },
      }).unwrap();
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <ReasonModal
      title={t("Extend the assessment window")}
      description={
        underUtilities
          ? t("The deadline moves from 10 to 15 working days from receipt (forsyningsforskriften § 22-4 (3)). Allowed once.")
          : t("The deadline moves from 10 to 15 working days from receipt (FOA § 26-6). Allowed once.")
      }
      placeholder={t("Why the assessment needs longer") ?? undefined}
      confirmLabel={t("Extend")}
      confirmColorScheme="teal"
      reason={reason}
      onReasonChange={setReason}
      isLoading={isLoading}
      onConfirm={submit}
      onClose={onClose}
    />
  );
};
